import React, { useState, useMemo, useEffect, useRef } from "react"
import useNewsVote from "./context"
import "./Translation.css"

const Translation = () => {
	const { data, handleChange, handleSaveTranslation } = useNewsVote()
	const translation = data?.translation || { th: [], cn: [{ key: "ตัวอย่าง", value: "示例" }] }

	const [selectedLang, setSelectedLang] = useState("cn")
	const [searchTerm, setSearchTerm] = useState("")
	const [editMode, setEditMode] = useState(null) // { index: number, key: string, value: string }
	const [newTranslation, setNewTranslation] = useState({ key: "", value: "" })
	const [error, setError] = useState("")
	const [isSaving, setIsSaving] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)

	// เก็บข้อมูลต้นฉบับเมื่อโหลดครั้งแรก
	const originalTranslation = useRef(JSON.parse(JSON.stringify(translation)))

	// อัพเดทข้อมูลต้นฉบับเมื่อมีการบันทึกสำเร็จ
	useEffect(() => {
		if (!isSaving) {
			originalTranslation.current = JSON.parse(JSON.stringify(translation))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSaving])

	// ฟังก์ชันกรองข้อมูลตามคำค้นหา
	const filteredTranslations = useMemo(() => {
		if (!searchTerm) return translation[selectedLang] || []

		return (translation[selectedLang] || []).filter(
			(item) => item.key.toLowerCase().includes(searchTerm.toLowerCase()) || item.value.toLowerCase().includes(searchTerm.toLowerCase())
		)
	}, [translation, selectedLang, searchTerm])

	// ตรวจสอบว่ามีการเปลี่ยนแปลงหรือไม่
	const getChanges = useMemo(() => {
		const changes = {
			added: {},
			modified: {},
			deleted: {},
			hasChanges: false,
		}

		const langs = ["th", "cn"]

		langs.forEach((lang) => {
			const original = originalTranslation.current[lang] || []
			const current = translation[lang] || []

			// หารายการที่เพิ่ม
			const added = current.filter((curr) => !original.some((orig) => orig.key === curr.key))
			if (added.length > 0) {
				changes.added[lang] = added
				changes.hasChanges = true
			}

			// หารายการที่ถูกลบ
			const deleted = original.filter((orig) => !current.some((curr) => curr.key === orig.key))
			if (deleted.length > 0) {
				changes.deleted[lang] = deleted
				changes.hasChanges = true
			}

			// หารายการที่แก้ไข
			const modified = current.filter((curr) => {
				const orig = original.find((o) => o.key === curr.key)
				return orig && orig.value !== curr.value
			})
			if (modified.length > 0) {
				changes.modified[lang] = modified.map((curr) => {
					const orig = original.find((o) => o.key === curr.key)
					return { key: curr.key, oldValue: orig.value, newValue: curr.value }
				})
				changes.hasChanges = true
			}
		})

		return changes
	}, [translation])

	// ตรวจสอบคำซ้ำ
	const checkDuplicate = (key, currentIndex = -1) => {
		return (translation[selectedLang] || []).some((item, index) => item.key === key && index !== currentIndex)
	}

	// เพิ่มคำแปลใหม่
	const handleAdd = () => {
		setError("")

		if (!newTranslation.key.trim() || !newTranslation.value.trim()) {
			setError("กรุณากรอกข้อมูลให้ครบถ้วน")
			return
		}

		if (checkDuplicate(newTranslation.key)) {
			setError("คำนี้มีอยู่ในระบบแล้ว")
			return
		}

		const updatedTranslation = {
			...translation,
			[selectedLang]: [...(translation[selectedLang] || []), { ...newTranslation }],
		}

		handleChange({ name: "translation", value: updatedTranslation })
		setNewTranslation({ key: "", value: "" })
		setError("")
	}

	// แก้ไขคำแปล
	const handleEdit = (index) => {
		const item = translation[selectedLang][index]
		setEditMode({ index, key: item.key, value: item.value })
		setError("")
	}

	// บันทึกการแก้ไข
	const handleSaveEdit = () => {
		setError("")

		if (!editMode.key.trim() || !editMode.value.trim()) {
			setError("กรุณากรอกข้อมูลให้ครบถ้วน")
			return
		}

		if (checkDuplicate(editMode.key, editMode.index)) {
			setError("คำนี้มีอยู่ในระบบแล้ว")
			return
		}

		const updatedList = [...translation[selectedLang]]
		updatedList[editMode.index] = { key: editMode.key, value: editMode.value }

		const updatedTranslation = {
			...translation,
			[selectedLang]: updatedList,
		}

		// setData({ ...data, translation: updatedTranslation })
		handleChange({ name: "translation", value: updatedTranslation })
		setEditMode(null)
		setError("")
	}

	// ยกเลิกการแก้ไข
	const handleCancelEdit = () => {
		setEditMode(null)
		setError("")
	}

	// ลบคำแปล
	const handleDelete = (index) => {
		if (window.confirm("คุณต้องการลบคำแปลนี้ใช่หรือไม่?")) {
			const updatedList = translation[selectedLang].filter((_, i) => i !== index)
			const updatedTranslation = {
				...translation,
				[selectedLang]: updatedList,
			}
			// setData({ ...data, translation: updatedTranslation })
			handleChange({ name: "translation", value: updatedTranslation })
		}
	}

	// แสดง Modal ยืนยันการบันทึก
	const handleShowConfirmModal = () => {
		if (!getChanges.hasChanges) {
			alert("ไม่มีการเปลี่ยนแปลงข้อมูล")
			return
		}
		setShowConfirmModal(true)
	}

	// บันทึกข้อมูลคำแปล
	const handleSaveAll = async () => {
		setIsSaving(true)
		try {
			// เรียก function บันทึกจาก context
			await handleSaveTranslation()

			// อัพเดทข้อมูลต้นฉบับ
			originalTranslation.current = JSON.parse(JSON.stringify(translation))

			setShowConfirmModal(false)
			alert("บันทึกข้อมูลสำเร็จ")
		} catch (error) {
			console.error("Error saving translation:", error)
			alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล")
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<div className="translation-container">
			<h2>จัดการคำแปล</h2>

			{/* Confirm Modal */}
			{showConfirmModal && (
				<div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>ยืนยันการบันทึกข้อมูล</h3>
							<button className="modal-close" onClick={() => setShowConfirmModal(false)}>
								×
							</button>
						</div>
						<div className="modal-body">
							<p className="modal-description">คุณได้ทำการเปลี่ยนแปลงข้อมูลดังต่อไปนี้:</p>

							{Object.keys(getChanges.added).length > 0 && (
								<div className="change-section">
									<h4 className="change-title added">
										<span role="img" aria-label="added">
											✅
										</span>{" "}
										รายการที่เพิ่ม
									</h4>
									{Object.entries(getChanges.added).map(([lang, items]) => (
										<div key={lang} className="change-lang-group">
											<strong>ภาษา{lang === "th" ? "ไทย" : "จีน"}:</strong>
											<ul>
												{items.map((item, idx) => (
													<li key={idx}>
														<span className="key-text">{item.key}</span> → <span className="value-text">{item.value}</span>
													</li>
												))}
											</ul>
										</div>
									))}
								</div>
							)}

							{Object.keys(getChanges.modified).length > 0 && (
								<div className="change-section">
									<h4 className="change-title modified">
										<span role="img" aria-label="modified">
											✏️
										</span>{" "}
										รายการที่แก้ไข
									</h4>
									{Object.entries(getChanges.modified).map(([lang, items]) => (
										<div key={lang} className="change-lang-group">
											<strong>ภาษา{lang === "th" ? "ไทย" : "จีน"}:</strong>
											<ul>
												{items.map((item, idx) => (
													<li key={idx}>
														<span className="key-text">{item.key}</span>:<span className="old-value"> {item.oldValue}</span> →
														<span className="new-value"> {item.newValue}</span>
													</li>
												))}
											</ul>
										</div>
									))}
								</div>
							)}

							{Object.keys(getChanges.deleted).length > 0 && (
								<div className="change-section">
									<h4 className="change-title deleted">
										<span role="img" aria-label="deleted">
											🗑️
										</span>{" "}
										รายการที่ลบ
									</h4>
									{Object.entries(getChanges.deleted).map(([lang, items]) => (
										<div key={lang} className="change-lang-group">
											<strong>ภาษา{lang === "th" ? "ไทย" : "จีน"}:</strong>
											<ul>
												{items.map((item, idx) => (
													<li key={idx}>
														<span className="key-text">{item.key}</span> → <span className="value-text">{item.value}</span>
													</li>
												))}
											</ul>
										</div>
									))}
								</div>
							)}
						</div>
						<div className="modal-footer">
							<button onClick={() => setShowConfirmModal(false)} className="btn btn-cancel" disabled={isSaving}>
								ยกเลิก
							</button>
							<button onClick={handleSaveAll} className="btn btn-confirm" disabled={isSaving}>
								{isSaving ? (
									<>
										<span className="spinner"></span>
										กำลังบันทึก...
									</>
								) : (
									"ยืนยันการบันทึก"
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* เลือกภาษา */}
			<div className="lang-selector">
				<label>เลือกภาษา: </label>
				<select
					value={selectedLang}
					onChange={(e) => {
						setSelectedLang(e.target.value)
						setSearchTerm("")
						setEditMode(null)
						setError("")
					}}
				>
					<option value="th">ไทย</option>
					<option value="cn">จีน</option>
				</select>
				<div style={{ flexGrow: 1 }} />
				<button
					onClick={handleShowConfirmModal}
					className={`btn btn-save-translation ${!getChanges.hasChanges ? "btn-disabled" : ""}`}
					disabled={!getChanges.hasChanges}
				>
					{isSaving ? "กำลังบันทึก..." : "บันทึกคำแปลทั้งหมด"}
				</button>
			</div>

			{/* ค้นหา */}
			<div className="search-box">
				<input
					type="text"
					placeholder="ค้นหาคำหรือคำแปล..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className="search-input"
				/>
			</div>

			{/* แสดง Error */}
			{error && <div className="error-message">{error}</div>}

			{/* เพิ่มคำแปลใหม่ */}
			<div className="add-section">
				<h3>เพิ่มคำแปลใหม่</h3>
				<div className="add-form">
					<input
						type="text"
						placeholder="คำภาษาไทย"
						value={newTranslation.key}
						onChange={(e) => setNewTranslation({ ...newTranslation, key: e.target.value })}
						className="input-field"
					/>
					<input
						type="text"
						placeholder={`คำแปลภาษา${selectedLang === "cn" ? "จีน" : "ไทย"}`}
						value={newTranslation.value}
						onChange={(e) => setNewTranslation({ ...newTranslation, value: e.target.value })}
						className="input-field"
					/>
					<button onClick={handleAdd} className="btn btn-add">
						เพิ่ม
					</button>
				</div>
			</div>

			{/* รายการคำแปล */}
			<div className="translation-list">
				<h3>รายการคำแปล ({filteredTranslations.length} รายการ)</h3>
				<table className="translation-table">
					<thead>
						<tr>
							<th>จัดการ</th>
							<th>#</th>
							<th>คำภาษาไทย</th>
							<th>คำแปล</th>
						</tr>
					</thead>
					<tbody>
						{filteredTranslations.length === 0 ? (
							<tr>
								<td colSpan="4" className="no-data">
									ไม่พบข้อมูล
								</td>
							</tr>
						) : (
							filteredTranslations.map((item, index) => {
								const originalIndex = translation[selectedLang].findIndex((t) => t.key === item.key && t.value === item.value)
								const isEditing = editMode?.index === originalIndex
								return (
									<tr key={originalIndex + "-" + index}>
										<td>
											{isEditing ? (
												<div className="btn-group">
													<button onClick={handleSaveEdit} className="btn btn-save">
														บันทึก
													</button>
													<button onClick={handleCancelEdit} className="btn btn-cancel">
														ยกเลิก
													</button>
												</div>
											) : (
												<div className="btn-group">
													<button onClick={() => handleEdit(originalIndex)} className="btn btn-edit">
														แก้ไข
													</button>
													<button onClick={() => handleDelete(originalIndex)} className="btn btn-delete">
														ลบ
													</button>
												</div>
											)}
										</td>
										<td>{originalIndex + 1}</td>
										<td>
											{isEditing ? (
												<input
													type="text"
													value={editMode.key}
													onChange={(e) => setEditMode({ ...editMode, key: e.target.value })}
													className="input-field-inline"
												/>
											) : (
												item.key
											)}
										</td>
										<td>
											{isEditing ? (
												<input
													type="text"
													value={editMode.value}
													onChange={(e) => setEditMode({ ...editMode, value: e.target.value })}
													className="input-field-inline"
												/>
											) : (
												item.value
											)}
										</td>
									</tr>
								)
							})
						)}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default Translation
